import { isLowPowerDevice, onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const STAGGER_SELECTOR =
  '.resource-list__item, .product-grid__item, .collection-card, .section-resource-list__header, .section-resource-list__content > .group-block, .section-content-wrapper > .group-block, .section-content-wrapper > comparison-slider-component, .featured-product-section .section-content-wrapper > *';

const MARQUEE_SELECTOR = 'marquee-component';

const STAGGER_STEP_MS = 90;
const MAX_STAGGER_MS = 540;

/**
 * Returns true when the element intersects the viewport.
 * @param {Element} element
 * @returns {boolean}
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.top < viewportHeight * 0.92 && rect.bottom > 0;
}

/**
 * @param {Element} section
 * @returns {HTMLElement[]}
 */
function getStaggerTargets(section) {
  return [...section.querySelectorAll(STAGGER_SELECTOR)].filter((target) => {
    if (!(target instanceof HTMLElement)) return false;
    return !target.closest(MARQUEE_SELECTOR);
  });
}

/**
 * Marks staggered child targets with sequential reveal delays.
 * @param {Element} section
 * @returns {HTMLElement[]}
 */
function applyStaggerDelays(section) {
  const staggerTargets = getStaggerTargets(section);

  staggerTargets.forEach((target, index) => {
    target.classList.add('scroll-reveal__target', 'scroll-reveal__target--stagger');

    const delay = Math.min(index * STAGGER_STEP_MS, MAX_STAGGER_MS);
    target.style.setProperty('--scroll-reveal-delay', `${delay}ms`);
  });

  return staggerTargets;
}

/**
 * Prepares a section for scroll reveal animation.
 * @param {Element} section
 * @param {number} index
 */
function prepareSection(section, index) {
  if (!(section instanceof HTMLElement)) return;

  if (section.querySelector(MARQUEE_SELECTOR)) {
    section.classList.add('scroll-reveal--skip');
    return;
  }

  section.classList.add('scroll-reveal');

  const staggerTargets = applyStaggerDelays(section);

  if (staggerTargets.length === 0) {
    const directChild = section.firstElementChild;

    if (directChild instanceof HTMLElement) {
      directChild.classList.add('scroll-reveal__target');
    }
  }

  if (index === 0 || isInViewport(section)) {
    section.classList.add('scroll-reveal--visible');
  }
}

/**
 * Initializes scroll reveal for homepage sections.
 */
function initScrollReveal() {
  if (document.body.dataset.scrollRevealEnabled !== 'true') return;
  if (document.body.dataset.template !== 'index') return;
  if (prefersReducedMotion() || isLowPowerDevice()) return;

  const main = document.querySelector('main.content-for-layout');
  if (!main) return;

  const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
  const sections = main.querySelectorAll(':scope > .shopify-section');

  sections.forEach((section, index) => {
    prepareSection(section, index);

    if (isDesignMode) {
      section.classList.add('scroll-reveal--visible');
    }
  });

  if (isDesignMode) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('scroll-reveal--visible');
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12,
    }
  );

  sections.forEach((section) => {
    if (section.classList.contains('scroll-reveal--visible') || section.classList.contains('scroll-reveal--skip')) {
      return;
    }

    observer.observe(section);
  });
}

onDocumentReady(initScrollReveal);
